import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../hooks";
import { Package, PackagePlus } from "lucide-react";
import type { LowStockProduct } from "../../types/sales";

interface Props {
  products: LowStockProduct[];
  onViewAll?: () => void;
}

export const LowStockProductsTable: React.FC<Props> = ({ products }) => {
  const navigate = useNavigate();
  const activeRole = useAppSelector((state) => state.auth.activeRole);
  const canRestock = activeRole === "Manager" || activeRole === "Admin";

  return (
    <div className="rounded-xl bg-white/80 border p-4 shadow-sm overflow-x-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-3">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-bold text-primary">
            Low Stock Products
          </h3>
          {products.length > 0 && (
            <span className="bg-red-100 text-red-700 text-xs font-medium px-2 py-0.5 rounded-full">
              {products.length} item{products.length !== 1 ? "s" : ""}
            </span>
          )}
        </div>

        {/* {onViewAll && (
          <button
            onClick={onViewAll}
            className="text-sm text-primary hover:underline"
          >
            View all products
          </button>
        )} */}
      </div>

      {/* Table */}
      <table className="w-full text-sm min-w-[600px]">
        <thead className="text-blue-900 border-b">
          <tr>
            <th className="text-left py-2">Product</th>
            <th className="text-left py-2">Store</th>
            <th className="text-left py-2">Current Qty</th>
            <th className="text-left py-2">Threshold</th>
            <th className="text-left py-2">Price</th>
            <th className="text-left py-2">Status</th>
            {canRestock && <th className="text-left py-2">Action</th>}
          </tr>
        </thead>

        <tbody>
          {products.length === 0 && (
            <tr>
              <td colSpan={canRestock ? 7 : 6} className="py-6 text-center text-gray-500">
                No low stock products
              </td>
            </tr>
          )}

          {products.map((p) => (
            <tr
              key={p.id}
              className="border-b last:border-none hover:bg-gray-50 transition"
            >
              <td className="py-2 font-medium">
                    <div className="flex items-center gap-2">
                      {p.image_url ? (
                        <img src={p.image_url} alt={p.name} className="w-8 h-8 rounded object-cover flex-shrink-0" />
                      ) : (
                        <Package size={20} className="text-gray-400 flex-shrink-0" />
                      )}
                      {p.name}
                    </div>
                  </td>
              <td className="py-2">{p.store}</td>
              <td className="py-2">
                <span className={`font-medium ${p.quantity === 0 ? "text-red-600" : "text-orange-600"}`}>
                  {p.quantity}
                </span>
              </td>
              <td className="py-2 text-gray-500">{p.threshold}</td>
              <td className="py-2">GHS {p.sellingPrice.toLocaleString()}</td>
              <td className="py-2">
                {p.quantity === 0 ? (
                  <span className="bg-red-100 text-red-700 text-xs font-medium px-2 py-1 rounded">
                    Out of Stock
                  </span>
                ) : (
                  <span className="bg-orange-100 text-orange-700 text-xs font-medium px-2 py-1 rounded">
                    Low Stock
                  </span>
                )}
              </td>
              {canRestock && (
                <td className="py-2">
                  <button
                    onClick={() => navigate(`/manager/items?productId=${p.id}&action=restock`)}
                    className="text-green-600 hover:text-green-700 flex items-center gap-1 text-xs font-medium"
                  >
                    <PackagePlus size={14} /> Restock
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default LowStockProductsTable;
